import React, { useCallback } from 'react';

import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import type { LuminaInteraction } from '../../interaction';
import { useLuminaInteraction } from '../../interaction-context';

type PeopleTeamsPayload = {
	action?: string;
	actionSubject?: string;
	actionSubjectId?: string;
	attributes?: {
		tab?: string;
	};
};

type PeoplePreviewAction = Extract<
	LuminaInteraction,
	{ type: 'peoplePreviewInteracted' }
>['action'];

const PROFILE_ACTIONS: Record<string, PeoplePreviewAction> = {
	collaboratorsSection: 'expandCollaborators',
	recentWorkSection: 'expandRecentWork',
	recentWorkViewAll: 'viewAllRecentWork',
	reportingLines: 'viewReportingLines',
	thirdPartyMessage: 'startMessage',
	viewProfile: 'viewProfile',
};

const INLINE_ACTIONS: Record<string, PeoplePreviewAction> = {
	reportingLines: 'viewReportingLines',
	thirdPartyMessage: 'startMessage',
	viewProfile: 'viewProfile',
};

const HIERARCHY_ACTIONS: Record<string, PeoplePreviewAction> = {
	managerNode: 'clickHierarchyNode',
	directReportNode: 'clickHierarchyNode',
	peerNode: 'clickHierarchyNode',
	overflow: 'viewReportingLines',
	viewFullReportingLine: 'viewReportingLines',
};

const ACTIONS_BY_SUBJECT: Record<string, Record<string, PeoplePreviewAction>> = {
	userProfilePreview: PROFILE_ACTIONS,
	userInlinePreview: INLINE_ACTIONS,
	hierarchyChart: HIERARCHY_ACTIONS,
};

const PROFILE_TABS = new Set(['collaborators', 'recentWork', 'reportingLine']);

export const getPeoplePreviewInteraction = (
	payload: PeopleTeamsPayload,
): LuminaInteraction | undefined => {
	if (payload.action !== 'clicked') {
		return undefined;
	}

	if (
		payload.actionSubject === 'userProfilePreview' &&
		payload.actionSubjectId === 'tab' &&
		PROFILE_TABS.has(payload.attributes?.tab ?? '')
	) {
		return { type: 'peoplePreviewInteracted', action: 'selectTab' };
	}

	const actions = ACTIONS_BY_SUBJECT[payload.actionSubject ?? ''];
	const action = actions?.[payload.actionSubjectId ?? ''];

	return action ? { type: 'peoplePreviewInteracted', action } : undefined;
};

export const PeopleInteractionListener = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const onInteraction = useLuminaInteraction();
	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const interaction = getPeoplePreviewInteraction(event.payload as PeopleTeamsPayload);
			if (interaction) {
				onInteraction?.(interaction);
			}
		},
		[onInteraction],
	);

	return (
		<AnalyticsListener channel="peopleTeams" onEvent={handleEvent}>
			{children}
		</AnalyticsListener>
	);
};
